import { useEffect } from "react";
import { AudioProvider, useStopAudio } from "../components/Audio";
import Frame from "../components/Frame";
import RetryPanel from "../components/RetryPanel";
import {
  CHOICE,
  COORDINATE_PLANE,
  COUNTING,
  EXPRESSION_ENTRY,
  MATCHING,
  NUMERIC_ENTRY,
  SEQUENCE,
  SORTING,
  TABLE_COMPLETION,
  TRACING,
} from "../data/activityAdapter";
import ChoiceStep from "./ChoiceStep";
import CoordinatePlaneStep from "./CoordinatePlaneStep";
import ExpressionEntryStep from "./ExpressionEntryStep";
import NumericEntryStep from "./NumericEntryStep";
import TableCompletionStep from "./TableCompletionStep";
import CountingStep from "./CountingStep";
import DrawingStep from "./DrawingStep";
import MatchingStep from "./MatchingStep";
import SequenceStep from "./SequenceStep";
import SortingStep from "./SortingStep";

// Each primitive has one generic screen, chosen only by the step's type.
// Drawing and tracing share the canvas.
const SCREENS = {
  [CHOICE]: ChoiceStep,
  [SEQUENCE]: SequenceStep,
  [MATCHING]: MatchingStep,
  [COUNTING]: CountingStep,
  [SORTING]: SortingStep,
  [NUMERIC_ENTRY]: NumericEntryStep,
  [EXPRESSION_ENTRY]: ExpressionEntryStep,
  [COORDINATE_PLANE]: CoordinatePlaneStep,
  [TABLE_COMPLETION]: TableCompletionStep,
};

// Moving to another step silences whatever was playing.
const StopOnStep = ({ stepId }) => {
  const stop = useStopAudio();
  useEffect(() => stop, [stepId, stop]);
  return null;
};

// Only the released task is on screen: no menu, no lesson list, no way out.
const Activity = ({ session }) => {
  const { activity, attempt, stepIndex, saving, retry, completeStep, saveProgress } =
    session;
  const step = activity.steps[stepIndex];
  const Screen = SCREENS[step.kind];

  return (
    <Frame progress={{ current: stepIndex + 1, total: activity.steps.length }}>
      <AudioProvider>
        <StopOnStep stepId={step.id} />
        {Screen ? (
          <Screen
            key={step.id}
            step={step}
            attemptId={attempt?.id}
            saving={saving}
            saved={attempt?.responses?.[step.id] ?? null}
            onDone={completeStep}
            onEstimate={saveProgress}
            onProgress={saveProgress}
          />
        ) : (
          <DrawingStep
            key={step.id}
            step={step}
            saved={attempt?.responses?.[step.id] ?? null}
            saving={saving}
            tracing={step.kind === TRACING}
            onDone={completeStep}
            onProgress={saveProgress}
            onUnavailable={session.unavailable}
          />
        )}
      </AudioProvider>
      {retry && <RetryPanel onRetry={retry} saving={saving} />}
    </Frame>
  );
};

export default Activity;
